import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import useFormHook from "@shared/hooks/useFormHook";
import { identificatorsService } from "@modules/additionalSettings/nomenclatures/services/identificators";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import { useLocation } from "react-router-dom";

export default function IdentificatorValuesForm({ onClose, nomenclature, modalType, onSuccess, determId, determMnemonic }) {
    const { modalRef } = useFormHook({}, onClose);
    const { t } = useTranslation();
    const { register, handleSubmit, setValue, reset } = useForm();
    const [isActiveChecked, setIsActiveChecked] = useState(false);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    // Extract determId and mnemonic from props, URL, or localStorage
    const getDetermId = () => {
        // First try prop, then URL, then localStorage
        return determId || new URLSearchParams(location.search).get('determId') || localStorage.getItem('selectedDetermId');
    };
    const getDetermMnemonic = () => {
        // First try prop, then URL, then localStorage
        return determMnemonic || new URLSearchParams(location.search).get('mnemonic') || localStorage.getItem('selectedDetermMnemonic');
    };
    // Set form values when editing
    useEffect(() => {
        if (modalType === 'edit' && nomenclature) {
            setValue('value', nomenclature.value);
            setValue('note', nomenclature.note);
            setIsActiveChecked(nomenclature.active || false);
            setValue('active', nomenclature.active || false);
        }
    }, [modalType, nomenclature, setValue]);
    const submitForm = async (data) => {
        const determId = getDetermId();
        const determMnemonic = getDetermMnemonic();
        
        if (!determId || !determMnemonic) {
            notifyError('No determ selected. Please go back and select a determ first.');
            return;
        }
        setLoading(true);
        try {
            const nomenclatureData = {
                mnemonic: determMnemonic, 
                value: data.value,        
                note: data.note,          
                active: data.active,
                determ_id: parseInt(determId)  
            };
            
            if (modalType === 'edit' && nomenclature) {
                await identificatorsService.updateNomenclature(nomenclature.id, nomenclatureData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else {
                await identificatorsService.createNomenclature(nomenclatureData);
                notifySuccess(t('common.createdSuccessfully'));
            }
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error saving nomenclature:', error);
            notifyError(error?.response?.data?.message || t('common.errorMessage'));
        } finally {
            setLoading(false);
        }
    }
    const handleClose = (e) => {
        if (modalRef.current === e.target) {
            onClose();
        }
    }
    const handleIsActiveChange = (e) => {
        setIsActiveChecked(e.target.checked);
        setValue('active', e.target.checked);
    }
    return (
        <div className="modal show" tabIndex="-1" onClick={handleClose}>
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {modalType === 'edit' ? t('common.edit') : t('common.add')} Nomenclature
                        </h4>
                        <form id="nomenclatureForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="flex-column">
                                <div className="form-section">
                                    <div className="form-group">
                                        <span className="span-bold">Mnemonic</span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            value={getDetermMnemonic() || ''}
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">Код <span className="required">*</span></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            {...register('value', { required: true })} 
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">Наименование</span>
                                        <textarea 
                                            className="form-control" 
                                            {...register('note')} 
                                            rows="3"
                                        />
                                    </div>
                                    <div className="flex-row">
                                        <span className="span-bold">
                                            Active
                                        </span>
                                        <div className="input-checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                {...register('active')}
                                                checked={isActiveChecked}
                                                onChange={handleIsActiveChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button 
                            type="submit" 
                            form="nomenclatureForm" 
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? t('common.saving') : t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
} 